import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import type { DocCategoryUpdatePayload } from "@xop/dashboard-shared";

import { updateCategory } from "./api.js";

/**
 * Rename + visibility editor for a single category.
 *
 * TWO INDEPENDENT AUDIENCES. `visible_in_customer_portal` governs the
 * customer KB; `visible_in_reseller_portal` governs the XOP Reseller partner
 * portal. They are deliberately separate switches rather than one "publish
 * externally" — an installation runbook belongs to neither, a price list
 * belongs to resellers only, and a datasheet belongs to both.
 *
 * Used from both the sidebar (per-row pencil button on a folder) and
 * the dedicated `/documentation/categories` admin list. PATCHes `/api/documentation/categories/:id` with whichever
 * fields actually changed — no-op when nothing changed so we don't
 * trigger a useless slug re-derivation on the Rails side.
 *
 * The visibility toggle duplicates the eye toggle on the sidebar's
 * row, but having it here lets an editor change both name + visibility
 * in one round-trip and gives `/documentation/categories` (which has
 * no inline eye toggle) the same capability.
 *
 * Implementation note: the visibility control stays a NATIVE
 * `<input type="checkbox">` wrapped in a `<label>` (rather than MUI's
 * Checkbox/FormControlLabel) because the colocated spec reaches it via
 * `getByText("Visible…").closest("label").querySelector("input[type=checkbox]")`.
 */
export function EditCategoryDialog({
  target,
  onClose,
  onSaved,
}: {
  target: {
    id: number;
    name: string;
    visible: boolean;
    /** Reseller-portal visibility. Optional so the older call sites that
     * have not been updated keep compiling; absent is read as `false`,
     * matching the server default. */
    visibleToResellers?: boolean;
  } | null;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element | null {
  // Hook order has to be stable across renders — keep all hooks above
  // the `null` early-return. Initial values lock in the moment the
  // dialog opens (keyed off `target.id`), then reset on close so the
  // next edit starts fresh.
  const [name, setName] = useState("");
  const [visible, setVisible] = useState(true);
  const [resellerVisible, setResellerVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initializedId = useRef<number | null>(null);

  if (target && initializedId.current !== target.id) {
    initializedId.current = target.id;
    setName(target.name);
    setVisible(target.visible);
    setResellerVisible(target.visibleToResellers === true);
    setError(null);
  }
  if (!target && initializedId.current !== null) {
    initializedId.current = null;
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error("No category to edit");
      const trimmed = name.trim();
      const document_category: DocCategoryUpdatePayload["document_category"] = {};
      if (trimmed && trimmed !== target.name) document_category.name = trimmed;
      if (visible !== target.visible) {
        document_category.visible_in_customer_portal = visible;
      }
      if (resellerVisible !== (target.visibleToResellers === true)) {
        document_category.visible_in_reseller_portal = resellerVisible;
      }
      // No-op edit: nothing actually changed. Skip the network call so
      // we don't ask Rails to re-derive the slug from the same string.
      if (Object.keys(document_category).length === 0) return null;
      return updateCategory(target.id, { document_category });
    },
    onSuccess: () => {
      setError(null);
      onSaved();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not update category");
    },
  });

  if (!target) return null;

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={!!target} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography component="span" sx={{ display: "block", fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>
          Rename category
        </Typography>
        <Typography component="span" sx={{ display: "block", fontSize: 12, fontWeight: 400, color: "text.disabled" }}>
          Updates the bucket name + visibility. Slug auto-re-derives from the new name on the Rails side.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{ display: "grid", gap: 2, pt: 1 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            mutation.mutate();
          }}
        >
          <Box sx={{ display: "grid", gap: 0.75 }}>
            <TextField
              id="edit-category-name"
              label="Name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Release notes"
              inputProps={{ required: true }}
              fullWidth
            />
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              Renaming re-slugs the category — any deep link to the
              previous slug will break. Existing docs keep their
              association.
            </Typography>
          </Box>

          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5),
              p: 1.5,
              fontSize: 14,
            }}
          >
            <Box
              component="input"
              type="checkbox"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
              sx={{ mt: 0.25 }}
            />
            <Box component="span">
              <Box component="span" sx={{ fontWeight: 500 }}>
                Visible in customer portal
              </Box>
              <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.disabled" }}>
                When off, the category and its published docs are hidden
                from the customer KB sidebar.
              </Box>
            </Box>
          </Box>

          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5),
              p: 1.5,
              fontSize: 14,
            }}
          >
            <Box
              component="input"
              type="checkbox"
              checked={resellerVisible}
              onChange={(e) => setResellerVisible(e.target.checked)}
              data-testid="category-visible-resellers"
              sx={{ mt: 0.25 }}
            />
            <Box component="span">
              <Box component="span" sx={{ fontWeight: 500 }}>
                Visible in reseller portal
              </Box>
              <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.disabled" }}>
                Independent of the customer switch above. When on, XOP Any
                Time partners can read this folder&rsquo;s published docs at
                reseller.xopnetworks.com. Off by default — only tick it for
                material you are happy for a partner to hold.
              </Box>
            </Box>
          </Box>

          {error && (
            <Box
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: "error.main",
                bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                px: 1.5,
                py: 1,
                fontSize: 12,
                color: "error.main",
              }}
            >
              {error}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              borderTop: 1,
              borderColor: "divider",
              mx: -3,
              mb: -2,
              px: 3,
              py: 1.5,
            }}
          >
            <Button type="button" variant="text" color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!name.trim() || mutation.isPending}>
              {mutation.isPending ? "Saving…" : "Save changes"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
